import React from 'react';
import { 
  X, Globe, Sun, Moon, User, LogOut, Heart, 
  Home, Grid, Award, ShieldAlert, PhoneCall, MessageSquare, Truck,
  Volume2, Smartphone, Palette, Type
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { loginWithGoogle, logoutUser } from '../lib/firebase';
import { triggerTapFeedback, triggerSuccessFeedback } from '../utils/feedback';

import ClearCacheButton from './ClearCacheButton';

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MenuDrawer({ isOpen, onClose }: MenuDrawerProps) {
  const {
    lang,
    setLang,
    theme,
    setTheme,
    user,
    isAdmin,
    isEditor,
    isOwner,
    activeTab,
    setActiveTab,
    whatsappNumber,
    supportPhone,
    isSoundEnabled,
    setIsSoundEnabled,
    soundVolume,
    setSoundVolume,
    isHapticEnabled,
    setIsHapticEnabled,
    hapticIntensity,
    setHapticIntensity,
    showThemePalette,
    setShowThemePalette,
    selectedFont,
    setSelectedFont,
    fontSize,
    setFontSize
  } = useApp();

  const isDark = theme === 'dark';
  const isCmsAuthorized = isAdmin || isEditor || isOwner;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden font-sans">
      
      {/* Dark dynamic backdrop with soft blur and click-to-close */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300 animate-fade-in cursor-pointer"
        onClick={onClose}
      />

      {/* Floating Side Drawer with Premium Contrast Backdrop */}
      <div 
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        className={`absolute inset-y-0 left-0 max-w-[320px] sm:max-w-sm w-full glass-panel border-r border-white/40 dark:border-white/10 shadow-[10px_0_40px_rgba(0,0,0,0.05)] dark:shadow-[10px_0_40px_rgba(var(--theme-rgb),0.1)] flex flex-col h-full animate-slide-right transition-all duration-300`}
      >
        
        {/* Header - Crisp and Clean Glassmorphic */}
        <div className="p-5 border-b border-neutral-200/60 dark:border-white/[0.06] flex items-center justify-between bg-transparent">
          <div className="flex items-center gap-2 select-none">
            <span className="text-xl font-black tracking-widest text-primary drop-shadow-[0_0_12px_rgba(var(--theme-rgb),0.3)] uppercase">
              Priyofy Menu
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-2.5 rounded-full bg-neutral-150/40 hover:bg-neutral-250/60 dark:bg-white/5 text-neutral-800 dark:text-neutral-200 hover:text-primary border border-neutral-200/50 dark:border-white/[0.06] hover:border-primary/35 transition-all cursor-pointer active:scale-95 animate-pulse-slow"
            aria-label="Close Menu"
          >
            <X className="w-5 h-5 stroke-[2.2px]" />
          </button>
        </div>

        {/* Menu Items - Clean & Scrollable Translucent Grid */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 scrollbar-none">
          
          {/* 1. Account / Safe Login Profile Section */}
          <div className="p-4 rounded-2xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03] space-y-3 shadow-xs">
            {user ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    <span className="block text-xs font-black text-neutral-800 dark:text-neutral-200 truncate">
                      {user.displayName || (lang === 'en' ? 'Priyofy Customer' : 'প্রিয়ফি গ্রাহক')}
                    </span>
                    <span className="block text-[10px] text-neutral-500 truncate">
                      {user.email}
                    </span>
                    {isCmsAuthorized && (
                      <span className="inline-flex mt-1 text-[9px] font-black uppercase bg-primary/15 text-primary border border-primary/20 px-2 py-0.5 rounded shadow-sm">
                        👑 {isOwner ? 'Owner' : isEditor ? 'Editor' : 'Admin'}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={async () => {
                    if (confirm(lang === 'en' ? 'Are you sure you want to sign out safely?' : 'আপনি কি সত্যি লগআউট করতে চান?')) {
                      await logoutUser();
                      onClose();
                    }
                  }}
                  className="w-full py-2 px-3.5 rounded-full bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/15 hover:border-rose-500/35 text-rose-500 text-xs font-black tracking-wide flex items-center justify-center gap-2 transition-all cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  {lang === 'en' ? 'Logout Securely' : 'সুরক্ষিত লগআউট'}
                </button>
              </div>
            ) : (
              <div className="space-y-2.5 text-center py-2">
                <div className="flex justify-center mb-1">
                  <User className="w-8 h-8 text-neutral-400 dark:text-neutral-500" />
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed font-bold">
                  {lang === 'en' ? 'Sign in to Your Account' : 'প্রোফাইলে লগইন করুন'}
                </p>
                <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
                  {lang === 'en' ? 'Keep track of orders, delivery speed and luxury rewards.' : 'আপনার সকল অর্ডার এবং ডেলিভারি সহজে ট্র্যাক করতে লগইন করুন।'}
                </p>
                <button
                  onClick={async () => {
                    try {
                      await loginWithGoogle();
                      onClose();
                    } catch (e: any) {
                      alert('Google Authentication Failed: ' + e.message);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 bg-primary hover:bg-primary/90 text-white font-extrabold text-xs tracking-wide shadow-[0_4px_16px_rgba(var(--theme-rgb),0.3)] transition-all cursor-pointer transform active:scale-95"
                >
                  {lang === 'en' ? 'Sign in with Google' : 'গুগল দিয়ে লগইন করুন'}
                </button>
              </div>
            )}
          </div>

          {/* 2. Standard Store Navigation Directory */}
          <div className="space-y-2.5">
            <span className="block text-[11px] font-extrabold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider pl-1">
              {lang === 'en' ? 'Navigation Chapters' : 'মেনু নেভিগেশন'}
            </span>
            <div className="space-y-1.5">
              {[
                { tab: 'home', text: lang === 'en' ? 'Home Showcase' : 'মূল হোমপেজ', icon: Home },
                { tab: 'categories', text: lang === 'en' ? 'Browse Categories' : 'ক্যাটাগরি সমূহ', icon: Grid },
                { tab: 'free_delivery', text: lang === 'en' ? 'Free Delivery' : 'ফ্রি ডেলিভারি', icon: Truck },
                { tab: 'deals', text: lang === 'en' ? 'Special Offers' : 'কম্বো ও অফারসমূহ', icon: Award },
                { tab: 'orders', text: isCmsAuthorized ? (lang === 'en' ? 'Admin Controller' : 'এডমিন কন্ট্রোলার') : (lang === 'en' ? 'My Orders & Profile' : 'আমার অর্ডার ও প্রোফাইল'), icon: isCmsAuthorized ? ShieldAlert : User }
              ].map((item) => {
                const Icon = item.icon;
                const active = activeTab === item.tab;
                return (
                  <button
                    key={item.tab}
                    onClick={() => {
                      triggerTapFeedback();
                      setActiveTab(item.tab as any);
                      onClose();
                    }}
                    className={`w-full relative overflow-hidden flex items-center justify-between p-3.5 pl-5 rounded-full border text-xs text-left font-extrabold tracking-wide transition-all duration-300 cursor-pointer ${
                      active 
                        ? 'bg-[#FFF1F2] dark:bg-primary/8 border-rose-200/50 dark:border-primary/20 text-primary shadow-[0_2px_12px_rgba(var(--theme-rgb),0.04)]' 
                        : 'bg-neutral-50 dark:bg-white/[0.01] border-neutral-100 dark:border-white/[0.03] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/80 dark:hover:bg-neutral-900/60'
                    }`}
                  >
                    {active && (
                      <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-primary" />
                    )}
                    <div className="flex items-center gap-3">
                      <Icon className={`w-4.5 h-4.5 ${active ? 'text-primary' : 'text-neutral-600 dark:text-neutral-400'}`} strokeWidth={2} />
                      <span>{item.text}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. System Display Preferences (Language, Themes) */}
          <div className="space-y-2.5">
            <span className="block text-[11px] font-extrabold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider pl-1">
              {lang === 'en' ? 'Settings / সেটিংস' : 'সেটিংস ও সেটআপ'}
            </span>
            <div className="space-y-1.5">
              
              {/* Language Selection Row */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03]">
                <div className="flex items-center gap-2.5">
                  <Globe className="w-4.5 h-4.5 text-neutral-605" />
                  <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                    {lang === 'en' ? 'Change Language' : 'ভাষা পরিবর্তন'}
                  </span>
                </div>
                <button
                  onClick={() => {
                    triggerSuccessFeedback();
                    setLang(lang === 'en' ? 'bn' : 'en');
                  }}
                  className="p-1 px-3.5 rounded-full bg-neutral-100 dark:bg-neutral-900 border border-primary text-primary text-[10px] font-black transition-all cursor-pointer hover:bg-neutral-200/50"
                >
                  {lang === 'en' ? 'বাংলা' : 'English'}
                </button>
              </div>

              {/* Theme Engine Toggle Row */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03]">
                <div className="flex items-center gap-2.5">
                  <Palette className="w-4.5 h-4.5 text-primary" />
                  <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                    {lang === 'en' ? 'Theme Engine Control' : 'থিম ইঞ্জিন চালু/বন্ধ'}
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (typeof setShowThemePalette !== 'undefined') {
                      setShowThemePalette(!showThemePalette);
                      triggerTapFeedback();
                    }
                  }}
                  className={`w-11 h-6.5 rounded-full p-0.5 transition-colors duration-300 ${showThemePalette ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-800'} cursor-pointer flex items-center`}
                  aria-label="Toggle Theme Engine"
                >
                  <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${showThemePalette ? 'translate-x-4.5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Theme/Mode Toggle Row */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03]">
                <div className="flex items-center gap-2.5">
                  {isDark ? <Moon className="w-4.5 h-4.5 text-primary" /> : <Sun className="w-4.5 h-4.5 text-primary" />}
                  <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                    {lang === 'en' ? 'Dark Mode Screen' : 'ডার্ক মোড স্ক্রিন'}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setTheme(isDark ? 'light' : 'dark');
                    triggerTapFeedback();
                  }}
                  className={`w-11 h-6.5 rounded-full p-0.5 transition-colors duration-300 ${isDark ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-800'} cursor-pointer flex items-center`}
                  aria-label="Toggle Theme Preference"
                >
                  <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${isDark ? 'translate-x-4.5' : 'translate-x-0'}`} />
                </button>
              </div>



              {/* Tap Sound Toggle Row */}
              <div className="flex flex-col gap-2 p-3.5 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Volume2 className={`w-4.5 h-4.5 ${isSoundEnabled ? 'text-primary' : 'text-neutral-450'}`} />
                    <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                      {lang === 'en' ? 'Tap Sound Controls' : 'বাটন প্রেস সাউন্ড'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const nextVal = !isSoundEnabled;
                      setIsSoundEnabled(nextVal);
                      if (nextVal) {
                        setTimeout(() => triggerSuccessFeedback(), 20);
                      }
                    }}
                    className={`w-11 h-6.5 rounded-full p-0.5 transition-colors duration-300 ${isSoundEnabled ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-800'} cursor-pointer flex items-center`}
                    aria-label="Toggle Tap Sound Preference"
                  >
                    <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${isSoundEnabled ? 'translate-x-4.5' : 'translate-x-0'}`} />
                  </button>
                </div>
                {isSoundEnabled && (
                  <div className="mt-1 pb-1 transition-all">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-[10px] text-neutral-500 font-bold">
                        {lang === 'en' ? 'Sound Volume' : 'সাউন্ড ভলিউম'}
                      </span>
                      <span className="text-[10px] text-primary font-black font-mono">
                        {soundVolume}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="200"
                      step="10"
                      value={soundVolume}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setSoundVolume(val);
                        triggerTapFeedback();
                      }}
                      className="w-full accent-primary h-1.5 bg-neutral-205 dark:bg-neutral-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-[8px] text-neutral-400 font-bold mt-1">
                      <span>{lang === 'en' ? 'Low' : 'ধীর'}</span>
                      <span>{lang === 'en' ? 'Normal' : 'স্বাভাবিক'}</span>
                      <span>{lang === 'en' ? 'Loud' : 'উচ্চ শব্দ'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Haptic Vibration Toggle Row */}
              <div className="flex flex-col gap-2 p-3.5 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Smartphone className={`w-4.5 h-4.5 ${isHapticEnabled ? 'text-primary' : 'text-neutral-450'}`} />
                    <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                      {lang === 'en' ? 'Haptic Tap Vibration' : 'স্পর্শ ভাইব্রেশন (Haptic)'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const nextVal = !isHapticEnabled;
                      setIsHapticEnabled(nextVal);
                      if (nextVal) {
                        setTimeout(() => triggerSuccessFeedback(), 20);
                      } else {
                        triggerTapFeedback();
                      }
                    }}
                    className={`w-11 h-6.5 rounded-full p-0.5 transition-colors duration-300 ${isHapticEnabled ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-800'} cursor-pointer flex items-center`}
                    aria-label="Toggle Haptic Preference"
                  >
                    <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${isHapticEnabled ? 'translate-x-4.5' : 'translate-x-0'}`} />
                  </button>
                </div>
                {isHapticEnabled && (
                  <div className="mt-1 pb-1 transition-all">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-[10px] text-neutral-500 font-bold">
                        {lang === 'en' ? 'Vibration Strength' : 'ভাইব্রেশন স্পীড/সময়'}
                      </span>
                      <span className="text-[10px] text-primary font-black font-mono">
                        {hapticIntensity}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="250"
                      step="25"
                      value={hapticIntensity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setHapticIntensity(val);
                        triggerTapFeedback();
                      }}
                      className="w-full accent-primary h-1.5 bg-neutral-205 dark:bg-neutral-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-[8px] text-neutral-400 font-bold mt-1">
                      <span>{lang === 'en' ? 'Soft' : 'হালকা'}</span>
                      <span>{lang === 'en' ? 'Medium' : 'মাঝারি'}</span>
                      <span>{lang === 'en' ? 'Strong' : 'শক্তিশালী'}</span>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* 4. Support Desk & Helpline Hotlinks */}
          <div className="space-y-2.5">
            <span className="block text-[11px] font-extrabold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider pl-1">
              {lang === 'en' ? 'Support Desk & Helpline' : 'সহায়তা ও যোগাযোগ'}
            </span>
            <div className="p-4 rounded-xl bg-neutral-50 dark:bg-white/[0.01] border border-neutral-100 dark:border-white/[0.03] space-y-3.5 shadow-sm">
              <span className="block text-[10.5px] leading-relaxed text-neutral-500 dark:text-neutral-450 font-bold">
                {lang === 'en' ? 'Have any questions about Products delivery? Message or call us anytime.' : 'যেকোনো গ্যাজেট ও ডিজিটাল আইটেম বা প্রোডাক্টস ডেলিভারি সংক্রান্ত তথ্যের জন্য সরাসরি যোগাযোগ করুন।'}
              </span>

              <div className="grid grid-cols-2 gap-2 text-[10.5px]">
                <a
                  href={`tel:${supportPhone}`}
                  className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-full bg-neutral-100 hover:bg-neutral-205/85 dark:bg-white/5 text-neutral-800 dark:text-white font-extrabold border border-neutral-200/50 dark:border-white/10 transition-all cursor-pointer text-center text-xs"
                >
                  <PhoneCall className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                  {lang === 'en' ? 'Call Hotline' : 'ফোন করুন'}
                </a>
                <a
                  href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-full bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold border border-emerald-200/50 dark:border-emerald-500/15 transition-all cursor-pointer text-center text-xs"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-555" strokeWidth={2} />
                  WhatsApp
                </a>
                {/* One-Click Cache Clear Button */}
                <div className="pt-2 border-t border-neutral-200/50 dark:border-white/[0.04]">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-black text-neutral-700 dark:text-neutral-300">
                      {lang === 'en' ? 'PWA Cache & Refresh' : 'ক্যাশ রিফ্রেশ (PWA)'}
                    </span>
                    <span className="text-[9px] text-neutral-400">
                      {lang === 'en' ? 'Fix UI updates' : 'ক্যাশ ক্লিয়ার'}
                    </span>
                  </div>
                  <ClearCacheButton variant="full" lang={lang} className="w-full" />
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Brand Bottom Layer - Glassmorphic Transparent */}
        <div className="p-5 border-t border-neutral-200/60 dark:border-white/[0.06] text-center bg-transparent mt-auto">
          <span className="text-[10px] text-neutral-500 font-bold flex items-center justify-center gap-1.5">
            Made with <Heart className="w-3.5 h-3.5 text-primary fill-primary animate-pulse" /> by Priyofy © 2026
          </span>
        </div>

      </div>
    </div>
  );
}
